// Canadian Postal Code Validation Utilities

/**
 * Validates Canadian postal code format
 * Accepts formats: "K1A 0A6", "K1A0A6", "k1a 0a6", "k1a0a6"
 * @param {string} postalCode - The postal code to validate
 * @returns {boolean} - True if valid Canadian postal code format
 */
export const isValidCanadianPostalCode = (postalCode) => {
  if (!postalCode || typeof postalCode !== 'string') {
    return false;
  }

  // Remove spaces and convert to uppercase
  const cleaned = postalCode.replace(/\s/g, '').toUpperCase();
  
  // Canadian postal code pattern: A1A1A1 (letter, digit, letter, digit, letter, digit)
  // Must be exactly 6 characters
  const canadianPostalCodePattern = /^[A-Z]\d[A-Z]\d[A-Z]\d$/;
  
  return cleaned.length === 6 && canadianPostalCodePattern.test(cleaned);
};

/**
 * Formats a Canadian postal code to standard format (A1A 1A1)
 * @param {string} postalCode - The postal code to format
 * @returns {string} - Formatted postal code or original if invalid
 */
export const formatCanadianPostalCode = (postalCode) => {
  if (!postalCode || typeof postalCode !== 'string') {
    return postalCode;
  }

  // Remove spaces and convert to uppercase
  const cleaned = postalCode.replace(/\s/g, '').toUpperCase();
  
  // Only format if it's exactly 6 characters and matches Canadian pattern
  if (cleaned.length === 6 && /^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(cleaned)) {
    // Format as A1A 1A1
    return `${cleaned.slice(0, 3)} ${cleaned.slice(3)}`;
  }
  
  // If it's longer than 6 characters, truncate to 6 and try to format
  if (cleaned.length > 6) {
    const truncated = cleaned.slice(0, 6);
    if (/^[A-Z]\d[A-Z]\d[A-Z]\d$/.test(truncated)) {
      return `${truncated.slice(0, 3)} ${truncated.slice(3)}`;
    }
  }
  
  return postalCode; // Return original if not valid format
};

/**
 * Cleans a postal code for database storage (removes spaces, converts to uppercase)
 * @param {string} postalCode - The postal code to clean
 * @returns {string} - Cleaned postal code
 */
export const cleanPostalCodeForStorage = (postalCode) => {
  if (!postalCode || typeof postalCode !== 'string') {
    return postalCode;
  }

  return postalCode.replace(/\s/g, '').toUpperCase();
};

/**
 * Gets postal code validation error message
 * @param {string} postalCode - The postal code to validate
 * @returns {string|null} - Error message or null if valid
 */
export const getPostalCodeValidationError = (postalCode) => {
  if (!postalCode || postalCode.trim() === '') {
    return null; // Empty is allowed (optional field)
  }

  if (!isValidCanadianPostalCode(postalCode)) {
    return '6 characters: XXX XXX format, e.g., K1A 0A6';
  }

  return null;
};

export default {
  isValidCanadianPostalCode,
  formatCanadianPostalCode,
  cleanPostalCodeForStorage,
  getPostalCodeValidationError
};
